/**
 * Top toolbar: project identity, mode switching, undo/redo, save, and the status band.
 *
 * It is a fixed 32px strip and nothing in it may make it taller. Toasts and the database-blocked
 * banner used to render here, inside the same flex row, which is how a long message wrapped the
 * toolbar onto a second line and pushed the whole layout down (audit A3). They now live in
 * `notificationsPanel.ts`, over the viewport. What is left is state that belongs to the toolbar:
 * what is busy, what is being saved, and how many errors have piled up.
 */

import './toolbar.css';

import type { Controllers } from '../controllers/types';
// The library controller owns the "make this name unique" rule; the dialog below only pre-fills
// with it, so importing the pure helper beats keeping a second copy of the rule in the toolbar.
import { uniqueClipName } from '../controllers/libraryController';
import type { EditorContext } from '../context';
import { dialogs } from '../dialogs';
import { EDITOR_MODES, canEnterMode, modeRejectionReason } from '../index';
import type { EditorMode, EditorState, ProjectMeta, Store } from '../index';
import { button, el, icon, textInput, type IconName } from './dom';
import { openHelpDialog } from './helpDialog';
import { createPanel, runAsync, type PanelHandle } from './panel';

/** Mode segment labels + icons. Title case, never uppercase (`design/ui-design.md` §3). */
const MODE_UI: Record<(typeof EDITOR_MODES)[number], { label: string; icon: IconName }> = {
  model: { label: 'Model', icon: 'box' },
  rig: { label: 'Rig', icon: 'bone' },
  map: { label: 'Map', icon: 'map' },
  animate: { label: 'Animate', icon: 'film' },
  library: { label: 'Library', icon: 'layers' },
  export: { label: 'Export', icon: 'download' },
};

/** One string that changes whenever any mode button's enabled-ness changes. */
function modeSignature(state: EditorState): string {
  return EDITOR_MODES.map((mode) => (canEnterMode(state, mode) ? '1' : '0')).join('');
}

/**
 * The WORKFLOW's next step — the one mode button that gets the `next` underline.
 *
 * Deliberately not "the next enterable mode": LIBRARY and MODEL are always enterable, so that
 * definition would point at a mode the user is not supposed to go to next and the hint would be
 * worse than nothing. This answers "Model → Rig/Map → Animate", which is the sequence the mode
 * guards actually enforce and which nothing in the UI used to state (audit B4). Purely visual —
 * it never enables a button `canEnterMode` refused.
 */
export function nextMode(state: EditorState): EditorMode | null {
  const mode = state.mode;
  if (mode !== 'model' && mode !== 'rig' && mode !== 'map') return null;
  if (canEnterMode(state, 'animate')) return 'animate';
  if (mode !== 'model') return null;
  const project = state.project;
  if (!project || !project.loaded) return null;
  const step: EditorMode = project.kind === 'unrigged' ? 'rig' : 'map';
  return canEnterMode(state, step) ? step : null;
}

/**
 * "Save as": ask for a name, pre-filled with the same "<clip> copy" the controller would generate,
 * then save the open clip under it. Cancel (null) and a blank answer both mean "do nothing" —
 * neither is a request to save.
 *
 * Exported because the Ctrl+Shift+S shortcut in `../app.ts` must do EXACTLY this, dialog included:
 * a shortcut that silently invents a name where the button asks for one is a different feature
 * wearing the same label. One implementation, two triggers.
 *
 * The store is read at trigger time, never at render time: the toolbar does not re-render on
 * library changes, so a snapshot taken during render could suggest a name that is taken by now.
 */
export function promptSaveClipAs(store: Store, controllers: Controllers): void {
  const state = store.getState();
  const clip = state.clip;
  if (!clip) return;
  const suggestion = uniqueClipName(
    `${clip.name} copy`,
    state.library.clips.map((c) => c.name),
  );
  void dialogs
    .text({ title: 'Save clip as', label: 'Name', value: suggestion, confirmLabel: 'Save' })
    .then((name) => {
      // The DOM backend trims already; a stubbed one may not, and an untrimmed name would create
      // a library entry the user cannot tell apart from the original.
      const trimmed = name === null ? '' : name.trim();
      if (trimmed) runAsync(controllers.library.saveClipAs(trimmed));
    });
}

export function mountToolbar(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store } = ctx;

  function renameProject(project: ProjectMeta, name: string): void {
    const trimmed = name.trim();
    if (!trimmed || trimmed === project.name) return;
    store.actions.setProject({ ...project, name: trimmed });
    runAsync(controllers.project.saveProjectState());
  }

  /**
   * Place the error log under its badge.
   *
   * `.errors-pop` is `position: fixed` because both `#toolbar` and `.panel.toolbar` are
   * `overflow: hidden` — that is what keeps the band at 32px — so an absolutely positioned child
   * would be clipped to a 32px strip. Fixed positioning escapes the clip, at the price of having
   * to place it by hand from the summary's rect.
   */
  function positionPopover(summary: HTMLElement, pop: HTMLElement): void {
    const rect = summary.getBoundingClientRect();
    const viewportWidth = typeof window === 'undefined' ? 0 : window.innerWidth;
    /*
     * The badge is 22px inside a 32px band, so `summary.bottom + 4` lands 1.2px ABOVE the
     * toolbar's own bottom edge and the popover clips into the band it belongs to. Clear the band
     * itself, not the badge: measured 33.8 vs 35 by the app tester.
     */
    const band = (container.closest('#toolbar') ?? container).getBoundingClientRect();
    pop.style.top = `${Math.max(rect.bottom + 4, band.bottom + 2)}px`;
    pop.style.left = 'auto';
    // Right-aligned to the badge: the log grows leftwards, away from the window edge.
    pop.style.right = `${Math.max(4, viewportWidth - rect.right)}px`;
  }

  function render(root: HTMLElement): void {
    const state = store.getState();

    root.appendChild(el('span', { class: 'appname' }, [icon('logo'), 'Motion Kit']));
    root.appendChild(el('span', { class: 'divider' }));

    root.appendChild(
      state.project
        ? textInput(state.project.name, (name) => renameProject(state.project as ProjectMeta, name), {
            class: 'projname',
            title: 'Project name',
          })
        : el('span', { class: 'projname empty', text: 'no project' }),
    );

    const suggested = nextMode(state);
    const modes = el('span', { class: 'modes' });
    for (const mode of EDITOR_MODES) {
      const allowed = mode === state.mode || canEnterMode(state, mode);
      const ui = MODE_UI[mode];
      const isNext = mode === suggested && mode !== state.mode;
      modes.appendChild(
        button(
          [icon(ui.icon), ui.label],
          () => {
            store.actions.setMode(mode);
          },
          {
            class: `${mode === state.mode ? 'mode on' : 'mode'}${isNext ? ' next' : ''}`,
            'data-mode': mode,
            disabled: !allowed,
            title: isNext
              ? 'Next step'
              : allowed
                ? `Switch to ${mode.toUpperCase()}`
                : modeRejectionReason(state, mode),
          },
        ),
      );
    }
    root.appendChild(modes);

    root.appendChild(el('span', { class: 'spacer' }));

    root.appendChild(
      button([icon('undo')], () => store.actions.undo(), {
        class: 'iconly',
        disabled: !state.history.canUndo,
        title: state.history.undoLabel ? `Undo ${state.history.undoLabel}` : 'Nothing to undo',
        'data-action': 'undo',
      }),
    );
    root.appendChild(
      button([icon('redo')], () => store.actions.redo(), {
        class: 'iconly',
        disabled: !state.history.canRedo,
        title: state.history.redoLabel ? `Redo ${state.history.redoLabel}` : 'Nothing to redo',
        'data-action': 'redo',
      }),
    );

    /*
     * The dirty marker stays the `●` glyph rather than becoming a CSS dot: the label text is what
     * the shell test asserts on, and a coloured span keeps `textContent` identical while giving
     * the mockup's orange dot (`design/ui-design.md` §4 / §6).
     */
    root.appendChild(
      button(
        state.clipDirty
          ? [el('span', { class: 'dirty', text: '●' }), 'Save clip']
          : [icon('save'), 'Save clip'],
        () => runAsync(controllers.library.saveCurrentClip()),
        {
          disabled: !state.clip,
          title: state.clipDirty ? 'Unsaved changes (Ctrl+S)' : 'Clip saved (Ctrl+S)',
          'data-action': 'save-clip',
        },
      ),
    );

    /*
     * "Save as" sits next to Save and is gated by exactly the same condition: it is the same
     * action with a different destination. The dialog is pre-filled with the name the controller
     * would have picked anyway, so pressing Enter is a valid answer.
     */
    root.appendChild(
      button([icon('copy'), 'Save as'], () => promptSaveClipAs(store, controllers), {
        disabled: !state.clip,
        title: 'Save the clip as a new library entry (Ctrl+Shift+S)',
        'data-action': 'save-clip-as',
      }),
    );

    /*
     * No global "Export GLB" button: exporting is EXPORT mode's job now, and a toolbar shortcut
     * that skipped the filename and the embed list was a second, silently different feature with
     * the same name.
     */

    root.appendChild(el('span', { class: 'divider' }));

    if (state.ui.busy) {
      root.appendChild(
        el('span', { class: 'busy', title: state.ui.busy, text: `… ${state.ui.busy}` }),
      );
    }

    /*
     * "Saving…" is not decoration: an unload cannot await IndexedDB, so this is the only honest
     * answer to "is my work on disk?" (audit P0). It is absent — not merely styled differently —
     * when nothing is outstanding, so a test can assert on its presence.
     */
    if (state.ui.saving) {
      root.appendChild(
        el('span', {
          class: 'saving',
          text: 'Saving…',
          title: 'A change is still being written to local storage.',
          'data-state': 'saving',
        }),
      );
    }

    if (state.ui.errors.length > 0) {
      const list = el('ul', { class: 'errlist' });
      // Newest first — the last failure is the one being investigated.
      for (const message of [...state.ui.errors].reverse()) {
        list.appendChild(el('li', { text: message }));
      }
      // `Errors (n)` exactly: the shell test compares `summary.textContent`, and an `<svg>`
      // contributes nothing to it.
      const summary = el('summary', { title: 'Show the error log' }, [
        icon('alert', 12),
        `Errors (${state.ui.errors.length})`,
      ]);
      const pop = el('div', { class: 'errors-pop' }, [
        list,
        button('Clear', () => store.actions.clearErrors()),
      ]);
      const details = el('details', { class: 'errors' }, [summary, pop]);
      details.addEventListener('toggle', () => {
        if (details.open) positionPopover(summary, pop);
      });
      root.appendChild(details);
    }

    root.appendChild(
      button([icon('help')], () => openHelpDialog(), {
        class: 'iconly',
        title: 'Keyboard shortcuts',
        'aria-label': 'Keyboard shortcuts',
        'data-action': 'help',
      }),
    );
  }

  const instance = createPanel(container, ctx, render, {
    className: 'toolbar',
    slices: [
      (s) => s.mode,
      (s) => s.project,
      (s) => s.history,
      (s) => s.clip,
      (s) => s.clipDirty,
      // Only the three `ui` fields the toolbar still renders. Subscribing to `ui` as a whole would
      // rebuild the strip on every toast — which, now that the errors log opens as a popover,
      // would close it under the user's cursor.
      (s) => s.ui.busy,
      (s) => s.ui.saving,
      (s) => s.ui.errors,
      modeSignature,
    ],
  });

  /*
   * A `<details>` popover has no light-dismiss of its own. Both listeners live on the panel (not
   * on the rebuilt subtree) so a rebuild cannot leak them.
   */
  function openDetails(): HTMLDetailsElement | null {
    return instance.root.querySelector<HTMLDetailsElement>('details.errors[open]');
  }

  const onDocumentPointerDown = (event: Event): void => {
    const details = openDetails();
    if (!details) return;
    if (event.target instanceof Node && details.contains(event.target)) return;
    details.open = false;
  };

  const onDocumentKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    const details = openDetails();
    if (details) details.open = false;
  };

  document.addEventListener('pointerdown', onDocumentPointerDown, true);
  document.addEventListener('keydown', onDocumentKeyDown);
  instance.own(() => {
    document.removeEventListener('pointerdown', onDocumentPointerDown, true);
    document.removeEventListener('keydown', onDocumentKeyDown);
  });

  return instance;
}
